import type { BufferGeometry } from 'three';
import type { LinearRGB } from '../../core/color';
import type { Rng } from '../../core/rng';
import type { Vec2, Vec3 } from '../../geo/types';
import { polylineLength, pointAtDistance } from '../../geo/polyline';
import type { MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { createMeshBuilder } from '../mesh/meshBuilder';
import { SURFACE } from '../mesh/attributes';
import { layoutToWorld } from '../../core/coords';
import type { PalisadeSegment } from './shipPlacement';
import { loftSolid, sparSolid, type Ring3 } from './shipSolids';

const PILE_TOP_Y_BASE = 1.0;
const PILE_TOP_Y_VARIANCE = 0.3;
export const PILE_RADIUS = 0.18;
const PILE_BOTTOM_Y = -3;
const PILE_SIDES = 6;
export const BEAM_Y_BOTTOM = 0.6;
const BEAM_Y_TOP = 0.85;
const BEAM_HALF_WIDTH = 0.125;
const MIN_SEGMENT_M = 0.01;
/* A mitre never reaches further than this many half-widths, whatever the turn. */
const MAX_MITRE = 4;

/*
 * The painting draws its pile rows as posts with water between them. Across 13 places on the rows
 * (painter frame, 1376 px) the line is 5.0 px wide at half its depth, 5.2 px in equivalent width,
 * its darkest L* is 21 and its depth below the water 13 L*. Posts drawn 0.64 m across at 1 m
 * spacing and 4.5 times their height match that at 22 places on the rendered rows: 4.5 px, 5.4 px,
 * L* 19 and 13.5 (overlapping 1.28 m piles 4 times their height drew a solid band 6.3 px wide).
 */
const PAINTED_PILE_DIAMETER = 0.64;

export const PALISADE_PAINTER_SCALE_H = PAINTED_PILE_DIAMETER / (2 * PILE_RADIUS);
export const PALISADE_PAINTER_SCALE_V = 4.5;

export interface PalisadeData {
  readonly id: string;
  readonly path: readonly Vec2[];
  readonly pileSpacing: number;
}

/** A stretch of a palisade left open, in metres along its path (boomHouse.ts). */
export interface PalisadeSpan {
  readonly from: number;
  readonly to: number;
}

const within = (s: number, spans: readonly PalisadeSpan[]): boolean =>
  spans.some((span) => s >= span.from && s <= span.to);

export interface PalisadeOutput {
  readonly segments: readonly PalisadeSegment[];
  readonly pileCount: number;
}

/* A pile in an opening still draws its height, so every other pile keeps the one it had. */
export function placePiles(
  palisade: PalisadeData,
  rng: Rng,
  openings: readonly PalisadeSpan[] = []
): readonly { readonly position: Vec2; readonly topY: number }[] {
  const piles: { position: Vec2; topY: number }[] = [];
  const pathLength = polylineLength(palisade.path);

  let s = 0;
  while (s <= pathLength) {
    const { point } = pointAtDistance(palisade.path, s);
    const topY = PILE_TOP_Y_BASE + rng.range(-PILE_TOP_Y_VARIANCE, PILE_TOP_Y_VARIANCE);
    if (!within(s, openings)) piles.push({ position: point, topY });
    s += palisade.pileSpacing;
  }

  return piles;
}

/* The stretches of a path of this length that no opening covers. */
function closedSpans(length: number, openings: readonly PalisadeSpan[]): readonly PalisadeSpan[] {
  const sorted = [...openings].sort((a, b) => a.from - b.from);
  const spans = sorted.reduce<{ readonly spans: readonly PalisadeSpan[]; readonly at: number }>(
    ({ spans: done, at }, o) => ({
      spans: o.from > at ? [...done, { from: at, to: Math.min(o.from, length) }] : done,
      at: Math.max(at, o.to),
    }),
    { spans: [], at: 0 }
  );
  const tail = spans.at < length ? [{ from: spans.at, to: length }] : [];
  return [...spans.spans, ...tail].filter((span) => span.to - span.from >= MIN_SEGMENT_M);
}

/* The part of a path between two distances along it. */
function subPath(path: readonly Vec2[], span: PalisadeSpan): readonly Vec2[] {
  const inner = path.filter((_, i) => {
    const s = polylineLength(path.slice(0, i + 1));
    return s > span.from && s < span.to;
  });
  const end = (s: number): Vec2 => pointAtDistance(path, s).point;
  return [end(span.from), ...inner, end(span.to)];
}

/** The stretches of a row that stand piles: its path, cut at every opening. */
export function closedRows(
  palisade: PalisadeData,
  openings: readonly PalisadeSpan[] = []
): readonly (readonly Vec2[])[] {
  const path = withoutRepeats(palisade.path);
  if (openings.length === 0) return [path];
  return closedSpans(polylineLength(path), openings).map((span) =>
    withoutRepeats(subPath(path, span))
  );
}

function woodStyle(color: LinearRGB, edgeId: number, seed: number): VertexStyle {
  return { color, surface: SURFACE.wood, edgeId, seed, strokeAngle: 0, extent: [0, 0] };
}

function withoutRepeats(path: readonly Vec2[]): readonly Vec2[] {
  return path.reduce<readonly Vec2[]>((kept, p) => {
    const last = kept[kept.length - 1];
    const apart = !last || Math.hypot(p[0] - last[0], p[1] - last[1]) >= MIN_SEGMENT_M;
    return apart ? [...kept, p] : kept;
  }, []);
}

function leftNormal(a: Vec2, b: Vec2): Vec2 {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  return [-(b[1] - a[1]) / len, (b[0] - a[0]) / len];
}

/* The offset from the path to the beam's left edge at each point, mitred at the joints. */
function mitreOffsets(path: readonly Vec2[]): readonly Vec2[] {
  return path.map((_, i) => {
    const before = i > 0 ? leftNormal(path[i - 1]!, path[i]!) : null;
    const after = i + 1 < path.length ? leftNormal(path[i]!, path[i + 1]!) : null;
    const [n0, n1] = [before ?? after!, after ?? before!];
    const sum: Vec2 = [n0[0] + n1[0], n0[1] + n1[1]];
    const len = Math.hypot(sum[0], sum[1]);
    const cos = Math.max((sum[0] * n0[0] + sum[1] * n0[1]) / len, 1 / MAX_MITRE);
    const reach = BEAM_HALF_WIDTH / cos;
    return [(sum[0] / len) * reach, (sum[1] / len) * reach];
  });
}

/* Right side up, across the top, left side down: the sweep direction crossed with the ring
   tangent points out of the beam, as loftSolid requires. */
function beamRing(p: Vec2, offset: Vec2): Ring3 {
  const left: Vec2 = [p[0] + offset[0], p[1] + offset[1]];
  const right: Vec2 = [p[0] - offset[0], p[1] - offset[1]];
  const at = (q: Vec2, y: number): Vec3 => layoutToWorld(q, y);
  return [
    at(right, BEAM_Y_BOTTOM),
    at(right, BEAM_Y_TOP),
    at(left, BEAM_Y_TOP),
    at(left, BEAM_Y_BOTTOM),
  ];
}

function loftBeam(builder: MeshBuilder, path: readonly Vec2[], style: VertexStyle): void {
  if (path.length < 2) return;
  const offsets = mitreOffsets(path);
  loftSolid(
    builder,
    style,
    path.map((p, i) => beamRing(p, offsets[i]!))
  );
}

/** The top beam along a palisade: a closed, mitred solid in world coordinates for each stretch
 *  between its openings. */
export function buildPalisadeBeam(
  builder: MeshBuilder,
  palisade: PalisadeData,
  woodColor: LinearRGB,
  edgeId: number,
  seed: number,
  openings: readonly PalisadeSpan[] = []
): void {
  const style = woodStyle(woodColor, edgeId, seed);
  closedRows(palisade, openings).forEach((row) => {
    loftBeam(builder, row, style);
  });
}

export function extractPalisadeSegments(palisade: PalisadeData): readonly PalisadeSegment[] {
  const segments: PalisadeSegment[] = [];
  const path = palisade.path;

  for (let i = 0; i < path.length - 1; i++) {
    segments.push({
      segment: { a: path[i]!, b: path[i + 1]! },
    });
  }

  return segments;
}

/** One pile, closed top and bottom; instances scale it vertically to their own top height. */
export function buildPileTemplate(
  woodColor: LinearRGB,
  edgeId: number,
  seed: number
): BufferGeometry {
  const builder = createMeshBuilder();
  sparSolid(builder, woodStyle(woodColor, edgeId, seed), {
    from: [0, PILE_BOTTOM_Y, 0],
    to: [0, PILE_TOP_Y_BASE, 0],
    radiusFrom: PILE_RADIUS,
    radiusTo: PILE_RADIUS,
    sides: PILE_SIDES,
    painterWidthScale: 1,
  });
  return builder.build();
}
